/**
 * Captures Google Ads (`gclid`, `wbraid`, `gbraid`), Microsoft Ads (`msclkid`),
 * and the `utm_*` params from the landing URL into the `mw_marketing_attribution`
 * cookie, scoped to the registrable parent domain so it is shared across
 * subdomains (marketing site → console). Checkout reads this cookie + the GA4
 * client id and forwards them on order placement, powering the ad-blocker-proof
 * server-side conversions (GA4 Measurement Protocol + Microsoft UET CAPI).
 * Tracking only — never affects pricing.
 *
 * Mirrors `affiliate-ref.ts` (90-day window, last-click wins).
 */
type MarketingAttribution = {
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  msclkid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
};

const ATTRIBUTION_KEYS: (keyof MarketingAttribution)[] = [
  'gclid',
  'wbraid',
  'gbraid',
  'msclkid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  const route = useRoute();
  const cookie = useCookie<MarketingAttribution | null>(
    'mw_marketing_attribution',
    {
      maxAge: 60 * 60 * 24 * 90, // 90-day click window
      sameSite: 'lax',
      path: '/',
      domain: config.public.cookieDomain || undefined,
      default: () => null,
    },
  );

  const capture = () => {
    const captured: MarketingAttribution = {};
    for (const key of ATTRIBUTION_KEYS) {
      const raw = route.query[key];
      const value = Array.isArray(raw) ? raw[0] : raw;
      if (typeof value === 'string' && value.trim()) {
        captured[key] = value.trim();
      }
    }
    // Last-click wins: only overwrite when this visit carried attribution.
    if (Object.keys(captured).length > 0) {
      cookie.value = captured;
    }
  };

  capture();
  watch(() => route.query, capture);
});
